import { loadSwiper } from './cg-load-swiper.js';

class GridSlider extends HTMLElement {
  constructor() {
    super();
    this.refreshBound = this.refresh.bind(this);
  }

  connectedCallback() {
    this.connection = (this.connection || 0) + 1;
    this.updateProperties();
    ['recentlyViewed:loaded', 'recommendations:loaded'].forEach(event => {
      document.addEventListener(event, this.refreshBound);
    });
    if (window.Shopify?.designMode) {
      window.addEventListener('resize', this.refreshBound);
    }
    this.initWhenNearViewport();
  }

  updateProperties() {
    this.slideCount = this.querySelectorAll('.swiper-slide').length;
    this.slidesPerViewDesktop = parseInt(this.dataset.slidesPerViewDesktop, 10) + 0.5 || 4.5;
    this.slidesPerViewTablet = 3.5;
    const mobileColumns = parseInt(this.dataset.slidesPerViewMobile, 10) || 2;
    this.slidesPerViewMobile = mobileColumns === 1 ? 1.08 : mobileColumns - 0.5;
    this.productsToShow = parseInt(this.dataset.productLimit, 10) || 4;
    ['Mobile', 'Tablet', 'Desktop'].forEach(size => {
      const key = `slidesPerView${size}`;
      if (this.slideCount <= this[key] || this[key] >= this.productsToShow) {
        this[key] = 'auto';
      }
    });
  }

  initWhenNearViewport() {
    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      this.init();
      return;
    }
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect();
        this.init();
      }
    }, { rootMargin: '600px 0px' });
    this.observer.observe(this);
  }

  refresh() {
    this.updateProperties();
    if (this.swiperInstance && !this.swiperInstance.destroyed) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = undefined;
      this.init();
    } else if (!this.initPromise) {
      this.initWhenNearViewport();
    }
  }

  init() {
    if (this.initPromise || this.swiperInstance || !this.isConnected) return;
    const connection = this.connection;
    const promise = loadSwiper().then(Swiper => {
      if (!this.isConnected || connection !== this.connection) return;
      this.updateProperties();
      const wrapper = this.closest('[data-grid-slider-wrapper]');
      this.swiperInstance = new Swiper(this, {
        spaceBetween: 15,
        slidesPerView: this.slidesPerViewMobile,
        loop: false,
        navigation: {
          prevEl: wrapper?.querySelector('.swiper-button--prev'),
          nextEl: wrapper?.querySelector('.swiper-button--next')
        },
        scrollbar: { el: wrapper?.querySelector('.swiper-scrollbar'), draggable: true },
        on: { init: () => wrapper?.classList.add('swiper-initialized') },
        breakpoints: {
          768: { spaceBetween: 30, slidesPerView: this.slidesPerViewTablet },
          1024: { spaceBetween: 30, slidesPerView: this.slidesPerViewDesktop }
        },
        watchOverflow: true
      });
      this.classList.remove('cg-grid-slider--pending');
    }).catch(error => {
      console.error('Casa Gomez: slider could not be initialized', error);
    }).finally(() => {
      if (this.initPromise === promise) this.initPromise = undefined;
    });
    this.initPromise = promise;
  }

  disconnectedCallback() {
    this.connection = (this.connection || 0) + 1;
    this.observer?.disconnect();
    ['recentlyViewed:loaded', 'recommendations:loaded'].forEach(event => {
      document.removeEventListener(event, this.refreshBound);
    });
    window.removeEventListener('resize', this.refreshBound);
    this.swiperInstance?.destroy(true, true);
    this.swiperInstance = undefined;
    this.initPromise = undefined;
  }
}

if (!customElements.get('grid-slider')) customElements.define('grid-slider', GridSlider);
